/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Table,
  Input,
  Select,
  DatePicker,
  Button,
  Drawer,
  Tag,
  message,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../axiosInstance";
import dayjs from "dayjs";
import DownloadTableData from "../../components/DownloadTableData";

const { RangePicker } = DatePicker;

const ExpiredServices = () => {
  const [data, setData] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [filters, setFilters] = useState({
    category: "all",
    sub_service: "",
    ll_valid: null,
    dl_valid: null,
    ic_valid: null,
    cf_valid: null,
    tax_valid: null,
    permit_valid: null,
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load sub services + fetch initial data
  useEffect(() => {
    const load = async () => {
      try {
        const subRes = await axiosInstance.get("/get_service_categories.php");
        if (subRes.data?.success) setSubServices(subRes.data.data);
      } catch (err) {
        console.error("Sub service load failed:", err);
      }
      fetchData(1, pagination.pageSize, true);
    };
    load();
  }, []);

  // search effect
  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, [searchText]);

  const getFilteredSubServices = () => {
    if (!subServices.length) return [];

    const filtered =
      filters.category === "all"
        ? subServices
        : subServices.filter(
            (s) => s.category.toLowerCase() === filters.category.toLowerCase()
          );

    // 🔥 Remove duplicate names
    const unique = [];
    const map = new Set();
    for (const item of filtered) {
      if (!map.has(item.name)) {
        map.add(item.name);
        unique.push(item);
      }
    }
    return unique;
  };

  const buildPayload = () => {
    const payload = {
      category: filters.category,
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    };

    if (searchText) payload.search = searchText;
    if (filters.sub_service) payload.sub_service = filters.sub_service;

    [
      "ll_valid",
      "dl_valid",
      "ic_valid",
      "cf_valid",
      "tax_valid",
      "permit_valid",
    ].forEach((f) => {
      const v = filters[f];
      if (v && Array.isArray(v) && v[0] && v[1]) {
        payload[f] = {
          from: v[0].format("YYYY-MM-DD"),
          to: v[1].format("YYYY-MM-DD"),
        };
      }
    });

    return payload;
  };

  const fetchData = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      setPagination((p) => ({ ...p, current: page, pageSize }));
      const payload = buildPayload();
      payload.limit = pageSize;
      payload.offset = (page - 1) * pageSize;

      const res = await axiosInstance.post("/expiring_services.php", payload);
      if (res.data?.success) {
        setData(res.data.data);
        setPagination((p) => ({ ...p, total: res.data.total }));
      } else {
        setData([]);
        message.info(res.data.message || "No records found");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch data");
      setData([]);
    }
    setLoading(false);
  };

  const onSearch = (v) => setSearchText(v);

  const resetFilters = () => {
    setFilters({
      category: "all",
      sub_service: "",
      ll_valid: null,
      dl_valid: null,
      ic_valid: null,
      cf_valid: null,
      tax_valid: null,
      permit_valid: null,
    });
    setSearchText("");
    setOpen(false);
    fetchData(1, pagination.pageSize, true);
  };

  // VEHICLE → map sub_service -> relevant validity field
  const getVehicleField = () => {
    switch (filters.sub_service) {
      case "Insurance":
        return "ic_valid";
      case "Fitness(CF)":
        return "cf_valid";
      case "Tax":
        return "tax_valid";
      case "National Permit":
        return "permit_valid";
      default:
        return null;
    }
  };

  const columns = [
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
      render: (t) => <span className="font-semibold text-blue-600">{t}</span>,
    },
    { title: "Phone", dataIndex: "phone", key: "phone" },

    {
      title: "Service",
      dataIndex: "category",
      key: "category",
      render: (t) => <Tag color={t === "licence" ? "blue" : "green"}>{t}</Tag>,
    },

    {
      title: "Sub Service",
      dataIndex: "sub_service",
      key: "sub_service",
      ellipsis: true,
    },

    {
      title: "Expiry",
      dataIndex: "expiry_value",
      key: "expiry_value",
      render: (v) =>
        v ? (
          <p className="font-bold text-red-500">
            {dayjs(v).format("YYYY-MM-DD")}
          </p>
        ) : (
          "N/A"
        ),
    },

    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      ellipsis: true,
    },
  ];

  return (
    <div className="bg-white p-4 rounded-xl shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Expired Services</h2>
          <p className="text-sm text-gray-500 mt-1">
            Total Records:{" "}
            <span className="font-semibold text-blue-600">
              {pagination.total}
            </span>
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchData(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={() => setOpen(true)}
          >
            Filter
          </Button>
          <DownloadTableData
            data={data}
            columns={columns}
            fileName="expired_services"
          />
        </div>
      </div>

      <Input
        placeholder="Search user id / name / phone..."
        prefix={<SearchOutlined />}
        allowClear
        value={searchText}
        onChange={(e) => onSearch(e.target.value)}
        style={{ width: 520, marginBottom: 12 }}
      />

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(r) => `${r.category}-${r.user_id}-${r.expiry_field || ""}`}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          onChange: (p, ps) => fetchData(p, ps),
        }}
      />

      <Drawer
        title="Filter Expiry"
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        width={380}
        footer={
          <div className="flex gap-2">
            <Button
              type="primary"
              block
              onClick={() => fetchData(1, pagination.pageSize)}
            >
              Apply
            </Button>
            <Button block onClick={resetFilters}>
              Reset
            </Button>
          </div>
        }
      >
        {/* CATEGORY */}
        <div className="mb-4">
          <label className="block mb-2">Service Category</label>
          <Select
            value={filters.category}
            onChange={(v) =>
              setFilters({ ...filters, category: v, sub_service: "" })
            }
            options={[
              { label: "All", value: "all" },
              { label: "Licence", value: "licence" },
              { label: "Vehicle", value: "vehicle" },
            ]}
            className="w-full"
          />
        </div>

        {/* SUB SERVICE */}
        <div className="mb-4">
          <label className="block mb-2">Sub Service</label>
          <Select
            allowClear
            className="w-full"
            placeholder="Select Sub Service"
            value={filters.sub_service || undefined}
            onChange={(v) => setFilters({ ...filters, sub_service: v })}
            options={getFilteredSubServices().map((s) => ({
              label: s.name,
              value: s.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </div>

        {/* LICENCE DATE FIELDS - ALWAYS SHOW BOTH */}
        {filters.category === "licence" && (
          <>
            <div className="mb-4">
              <label className="block mb-2">LL Valid</label>
              <RangePicker
                className="w-full"
                onChange={(v) => setFilters({ ...filters, ll_valid: v })}
              />
            </div>

            <div className="mb-4">
              <label className="block mb-2">DL Valid</label>
              <RangePicker
                className="w-full"
                onChange={(v) => setFilters({ ...filters, dl_valid: v })}
              />
            </div>
          </>
        )}

        {/* VEHICLE DATE FIELDS BASED ON SUB SERVICE */}
        {filters.category === "vehicle" &&
          (() => {
            const field = getVehicleField();

            if (!field)
              return (
                <p className="text-red-500 text-sm">
                  Select a vehicle sub-service to filter expiry date.
                </p>
              );

            return (
              <div className="mb-4">
                <label className="block mb-2">
                  {field.replace("_valid", "").toUpperCase()} Valid
                </label>
                <RangePicker
                  className="w-full"
                  onChange={(v) => setFilters({ ...filters, [field]: v })}
                />
              </div>
            );
          })()}
      </Drawer>
    </div>
  );
};

export default ExpiredServices;
